'use client';

/**
 * EntityAuditPanel Component
 *
 * Composable panel for entity detail pages showing audit history
 * with tabs for Timeline, Table, and Version comparison views.
 *
 * @see Issue #234 - Entity Audit Activity Logs
 */

import CompareIcon from '@mui/icons-material/CompareArrows';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import TableIcon from '@mui/icons-material/TableChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import { Box, Chip, IconButton, Tab, Tabs, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import type { NeonAccent } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import type { AuditExportConfig, EntityAuditPanelProps } from '../../types/audit';
import { AuditActivityTimeline } from '../AuditActivityTimeline/AuditActivityTimeline';
import { AuditDiffViewer } from '../AuditDiffViewer/AuditDiffViewer';
import { AuditLogTable } from '../AuditLogTable/AuditLogTable';
import { GlassCard } from '../GlassCard/GlassCard';
import { GlassTooltip } from '../GlassTooltip';
import { LoadingOverlay } from '../LoadingOverlay';

// ============================================================================
// Styled Components
// ============================================================================

const StyledHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
}));

const StyledTitleSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

const StyledActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

interface StyledTabsContainerProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}

const StyledTabsContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledTabsContainerProps>(({ theme, $isDark }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    borderRadius: 12,
    background: glass.background,
    border: `1px solid ${glass.border}`,
    marginBottom: theme.spacing(2),
  };
});

const StyledStatsBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledStat = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const StyledContentArea = styled(Box)<{ $height?: number | string }>(({ $height }) => ({
  height: $height || 'auto',
  overflow: 'auto',
}));

const StyledEmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  textAlign: 'center',
}));

// ============================================================================
// Component
// ============================================================================

export function EntityAuditPanel({
  entityType,
  entityId,
  entityName,
  tab = 'timeline',
  onTabChange,
  events = [],
  entries = [],
  versions = [],
  stats,
  loading = false,
  error,
  onLoadMore,
  hasMore = false,
  onRefresh,
  onExport,
  variant = 'glass',
  neonAccent,
  height,
  // DEPRECATED: defaultExpanded parameter kept for API compatibility. Will be removed in next major version.
  defaultExpanded: _defaultExpanded = true,
}: EntityAuditPanelProps) {
  void _defaultExpanded; // Silence unused variable warning
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useI18n();
  const [currentTab, setCurrentTab] = useState(tab);
  const [selectedVersionA, setSelectedVersionA] = useState<string | null>(null);
  const [selectedVersionB, setSelectedVersionB] = useState<string | null>(null);

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: 'timeline' | 'table' | 'versions') => {
    setCurrentTab(newValue);
    onTabChange?.(newValue);
  };

  // Handle export
  const handleExport = async (format: 'csv' | 'json') => {
    if (!onExport) return;
    const config: AuditExportConfig = {
      format,
      fileName: `audit-${entityType}-${entityId}-${new Date().toISOString().split('T')[0]}`,
    };
    await onExport(config);
  };

  // Build comparison from selected versions
  const comparison =
    selectedVersionA && selectedVersionB
      ? {
          versionA: versions.find((v) => v.id === selectedVersionA)!,
          versionB: versions.find((v) => v.id === selectedVersionB)!,
          changes: [], // TODO(#234): Compute field-level changes from actual version data or backend comparison endpoint
          summary: { totalChanges: 0, fieldsAdded: 0, fieldsRemoved: 0, fieldsModified: 0 },
        }
      : null;

  // Render tab content
  const renderTabContent = () => {
    // Error state
    if (error) {
      return (
        <StyledEmptyState>
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </StyledEmptyState>
      );
    }

    // Loading state
    if (loading && events.length === 0 && entries.length === 0) {
      return (
        <Box sx={{ position: 'relative', minHeight: 160 }}>
          <LoadingOverlay visible message={t('common.loading')} size={32} neonAccent={neonAccent} />
        </Box>
      );
    }

    switch (currentTab) {
      case 'timeline':
        return (
          <AuditActivityTimeline
            events={events}
            loading={loading}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            showAvatars
            relativeTime
            variant="minimal"
            neonAccent={neonAccent}
            showCard={false}
          />
        );

      case 'table':
        return (
          <AuditLogTable
            entries={entries}
            loading={loading}
            onExport={onExport}
            variant="minimal"
            neonAccent={neonAccent}
            density="compact"
          />
        );

      case 'versions':
        return (
          <Box>
            {/* Version Selection */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {t('audit.diff.from')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {versions.slice(0, 5).map((v) => (
                    <Chip
                      key={v.id}
                      label={new Date(v.createdAt).toLocaleDateString()}
                      size="small"
                      variant={selectedVersionA === v.id ? 'filled' : 'outlined'}
                      color={selectedVersionA === v.id ? 'error' : 'default'}
                      onClick={() => setSelectedVersionA(v.id)}
                    />
                  ))}
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {t('audit.diff.to')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {versions.slice(0, 5).map((v) => (
                    <Chip
                      key={v.id}
                      label={new Date(v.createdAt).toLocaleDateString()}
                      size="small"
                      variant={selectedVersionB === v.id ? 'filled' : 'outlined'}
                      color={selectedVersionB === v.id ? 'success' : 'default'}
                      onClick={() => setSelectedVersionB(v.id)}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
            {/* Diff Viewer */}
            <AuditDiffViewer comparison={comparison} loading={loading} variant="minimal" neonAccent={neonAccent} />
          </Box>
        );

      default:
        return null;
    }
  };

  // Main content
  const panel = (
    <Box>
      {/* Header */}
      <StyledHeader>
        <StyledTitleSection>
          <HistoryIcon color="primary" />
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
              }}
            >
              {t('audit.panel.title')}
            </Typography>
            {entityName && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {entityType}: {entityName}
              </Typography>
            )}
          </Box>
        </StyledTitleSection>
        <StyledActions>
          {onRefresh && (
            <GlassTooltip title={t('common.refresh')}>
              <IconButton size="small" onClick={onRefresh} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </GlassTooltip>
          )}
          {onExport && (
            <GlassTooltip title={t('audit.table.export')}>
              <IconButton size="small" onClick={() => handleExport('csv')}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </GlassTooltip>
          )}
        </StyledActions>
      </StyledHeader>

      {/* Stats Bar */}
      {stats && (
        <StyledStatsBar>
          <StyledStat>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
              }}
            >
              {stats.totalChanges}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('audit.stats.totalChanges')}
            </Typography>
          </StyledStat>
          <StyledStat>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
              }}
            >
              {stats.uniqueEditors}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('audit.stats.uniqueEditors')}
            </Typography>
          </StyledStat>
          {stats.lastChangedBy && (
            <StyledStat>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                }}
              >
                {stats.lastChangedBy.userName}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('audit.stats.lastEditor')}
              </Typography>
            </StyledStat>
          )}
        </StyledStatsBar>
      )}

      {/* Tabs */}
      <StyledTabsContainer $isDark={isDark} $neonAccent={neonAccent}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{
            minHeight: 42,
            '& .MuiTab-root': {
              minHeight: 42,
              textTransform: 'none',
            },
          }}
        >
          <Tab
            value="timeline"
            label={t('audit.tabs.timeline')}
            icon={<TimelineIcon fontSize="small" />}
            iconPosition="start"
          />
          <Tab value="table" label={t('audit.tabs.table')} icon={<TableIcon fontSize="small" />} iconPosition="start" />
          <Tab
            value="versions"
            label={t('audit.tabs.versions')}
            icon={<CompareIcon fontSize="small" />}
            iconPosition="start"
          />
        </Tabs>
      </StyledTabsContainer>

      {/* Content Area */}
      <StyledContentArea $height={height}>{renderTabContent()}</StyledContentArea>
    </Box>
  );

  // Wrap in card if needed
  if (variant === 'glass') {
    return <GlassCard neonAccent={neonAccent}>{panel}</GlassCard>;
  }

  return panel;
}

EntityAuditPanel.displayName = 'EntityAuditPanel';

export default EntityAuditPanel;
